import React, { useState, useEffect } from "react";
import { Grid2 as Grid, Typography } from "@mui/material";
import { GetState, GetCity } from "react-country-state-city";
import SelectInput from "../../../../components/ui/SelectInput";
import InputField1 from "../../../../components/ui/InputField1";
import { red } from "@mui/material/colors";
import { countries, timezoneMapping } from "../../../../utils/helper";
import { validateNumber } from "../../../../utils/velidations";


function Address({ errors, persistedData, countryRef, stateRef, cityRef, timezone }) {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedTimeZone, setSelectedTimeZone] = useState(null);
    const [stateList, setStateList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [filteredTimeZones, setFilteredTimeZones] = useState([]);

    const stateHasCities = stateList.find(s => s.id === selectedState?.id)?.hasCities ?? true;

    useEffect(() => {
        const countryId = persistedData?.country;
        const stateId = persistedData?.state;
        const cityId = persistedData?.city;
        const tzValue = persistedData?.timeZone;

        if (countryId) {
            const c = countries.find(item => String(item.id) === String(countryId));
            if (c) setSelectedCountry(c);
        }

        if (stateId) setSelectedState({ id: stateId });
        if (cityId) setSelectedCity({ id: cityId });

        if (tzValue) {
            setSelectedTimeZone({ name: tzValue, value: tzValue });
        }
    }, [persistedData]);

    useEffect(() => {
        if (selectedCountry?.id) {
            GetState(parseInt(selectedCountry.id)).then((result) => {
                setStateList(result);
                if (selectedState?.id && !selectedState.name) {
                    const fullState = result.find(s => String(s.id) === String(selectedState.id));
                    if (fullState) setSelectedState(fullState);
                }
            });

            const iso = selectedCountry.iso2;
            const rawZones = timezoneMapping[iso] || [];

            const formatted = rawZones.map(tz => ({ name: tz, value: tz }));

            setFilteredTimeZones(formatted);

            if (selectedTimeZone && !rawZones.includes(selectedTimeZone.value)) {
                setSelectedTimeZone(null);
            }
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (selectedCountry?.id && selectedState?.id) {
            GetCity(parseInt(selectedCountry.id), parseInt(selectedState.id)).then((result) => {
                setCitiesList(result);
                if (selectedCity?.id && !selectedCity.name) {
                    const fullCity = result.find(c => String(c.id) === String(selectedCity.id));
                    if (fullCity) setSelectedCity(fullCity);
                }
            });
        }
    }, [selectedCountry?.id, selectedState?.id, selectedCity?.id, selectedCity?.name]);

    const handleCountryChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedCountry(val || null);
        setSelectedState(null);
        setSelectedCity(null);
        setSelectedTimeZone(null);
    };

    const handleStateChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedState(val || null);
        setSelectedCity(null);
    };

    const handleCityChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedCity(val || null);
    };

    const handleTimeZoneChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedTimeZone(val || null);
    };

    return (
        <>
            <Grid size={12}>
                <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "other.color1", mb: 2, mt: 2 }}>
                    Address & Timezone Information
                </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={countryRef}
                    name="country"
                    label="Country *"
                    options={countries}
                    renderValueKey="name"
                    selectLabel="Select country"
                    varianttype="selectType1"
                    onChange={handleCountryChange}
                    value={selectedCountry || null}
                />
                {errors?.country && <Typography mt={1} color={red["500"]} >Please select a country</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={stateRef}
                    name="state"
                    label="State *"
                    options={stateList}
                    renderValueKey="name"
                    selectLabel="Select state"
                    varianttype="selectType1"
                    disabled={!selectedCountry}
                    onChange={handleStateChange}
                    value={selectedState || null}
                />
                {errors?.state && <Typography mt={1} color={red["500"]}>Please select a state</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={cityRef}
                    name="city"
                    label="City *"
                    options={citiesList}
                    renderValueKey="name"
                    selectLabel="Select city"
                    varianttype="selectType1"
                    disabled={!selectedState}
                    onChange={handleCityChange}
                    value={selectedCity || null}
                />
                {stateHasCities && errors?.city && <Typography mt={1} color={red["500"]}>Please select a city</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={timezone}
                    name="timeZone"
                    label="Timezone *"
                    options={filteredTimeZones}
                    renderValueKey="name"
                    varianttype="selectType1"
                    selectLabel="Select a timezone"
                    onChange={handleTimeZoneChange}
                    value={selectedTimeZone || null}
                    key={`tz-${selectedCountry?.id || 'none'}`}
                />
                {errors?.timeZone && (
                    <Typography mt={1} color={red["500"]} >
                        Please select time zone
                    </Typography>
                )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <InputField1
                    name="zip"
                    errors={errors}
                    label="Zip Code *"
                    errText={typeof errors?.zip === "string" ? errors.zip : "Please enter zip code"}
                    placeholder="Enter zip code"
                    defaultValue={persistedData?.zip || ""}
                    onChangeFun={validateNumber}
                    slotProps={{ inputProps: { maxLength: 6 } }}
                />

            </Grid>
        </>
    );
}

export default Address;